/*
 * Cálculo puro de saldos por comitente.
 *
 * Devuelve por cuenta:
 *   saldoSnapshot, saldoReal, saldoProyectado, movimientosReales[], movimientosPendientes[]
 *
 * Reglas:
 *   - Si snapshot activo es 'inicial', todos los movimientos del día Hechos impactan.
 *   - Si snapshot activo es 'intradiario', solo los movimientos con createdAt > uploadedAt impactan
 *     (los anteriores ya están reflejados en el snapshot del custodio).
 *   - Solo el estado 'Hecha' impacta saldoReal. Estados 'En Proceso' y 'Pendiente' solo afectan saldoProyectado.
 *   - Cauciones y Pases: las Hechas impactan por montoOperado (lo liquidado); las Pendientes/En Proceso por monto (lo cargado por el comercial).
 *     Un Pase impacta el efectivo igual que una caución (Tomadora suma, Colocadora resta) en la punta CI de hoy.
 *   - Instrucciones FCI Rescate T+1 solo impactan saldoReal si fechaLiquidacion <= hoy.
 *   - Acreditaciones esperadas (cupones, amortizaciones, letras, cheques MAV) que el operador marcó
 *     'Acreditada' impactan saldoReal como un movimiento ejecutado más. La hora de la marca
 *     hace de createdAt: con un intradiario activo, lo marcado antes ya está en el snapshot.
 *     Las pendientes o 'No llegó' no impactan, y las dollar linked tampoco salvo que el
 *     operador haya cargado el importe real en pesos al marcarlas (ver mergeAcreditaciones).
 *   - Licitaciones adjudicadas: restan lo adjudicado desde la fecha de caja hasta la víspera de
 *     la liquidación (la ventana y el monto los arma buildDebitosLicitaciones). Impactan saldoReal
 *     sin el filtro post-snapshot: el custodio no las debita hasta liquidar, así que ningún
 *     snapshot de esos días las refleja. Por lo mismo quedan fuera de conciliarSnapshots.
 */

import { toIsoDate } from './dateHelpers';
import {
    BUCKET_ARS,
    MONEDA_BUCKETS,
    MONEDA_MOVIMIENTO,
    SNAPSHOT_SALDO_FIELD,
    MONEDAS_CON_SALDO as MONEDAS_SEGUIDAS,
} from '../constants/monedas';

const toMillis = (ts) => {
    if (!ts) return 0;
    if (typeof ts.toMillis === 'function') return ts.toMillis();
    if (ts instanceof Date) return ts.getTime();
    return new Date(ts).getTime();
};

const isCaucionReal = (estado) => estado === 'Hecha' || estado === 'Hecha ';
const isOperacionReal = (estado) => estado === 'Hecha';
const isInstruccionReal = (estado) => estado === 'Hecha';
export const ESTADO_ACREDITACION_ACREDITADA = 'acreditada';
const isPendiente = (estado) => estado === 'En Proceso' || estado === 'En proceso' || estado === 'Pendiente';

const debeContarPostSnapshot = (movCreatedAt, snapshot) => {
    if (!snapshot) return true;
    if (snapshot.tipo === 'inicial') return true;
    const snapshotMs = toMillis(snapshot.uploadedAt);
    const movMs = toMillis(movCreatedAt);
    return movMs > snapshotMs;
};

const impactoCaucion = (caucion) => {
    const monto = Number(caucion.montoOperado) || 0;
    return caucion.operacion === 'Tomadora' ? monto : -monto;
};

const impactoOperacion = (operacion) => {
    const monto = Number(operacion.monto) || 0;
    return operacion.operacion === 'Venta' ? monto : -monto;
};

const operacionAfectaSaldo = (op) => {
    const especie = (op.especie || '').toString().toUpperCase();
    if (especie.startsWith('DLR/')) return false;
    if (op.esFuturos === true) return false;
    // Jefferies es custodia separada en el exterior: no impacta saldos locales
    if (op.mercado === 'JEFFERIES') return false;
    return true;
};

// Monedas con saldo seguido por la planilla: son las tres que trae el snapshot
// del custodio (pesos, dólar MEP y dólar cable).
export const MONEDAS_CON_SALDO = MONEDAS_SEGUIDAS;

/*
 * Impacto con signo de una instrucción sobre el saldo del día (0 si todavía no
 * liquida). Se exporta para que el preview del formulario proyecte el saldo con
 * exactamente la misma regla con la que después se calcula Control de Saldos.
 */
export const impactoInstruccion = (instr, fechaHoyIso) => {
    const monto = Number(instr.monto) || 0;
    if (!monto) return 0;
    if (instr.tipo === 'Ingreso') return monto;
    if (instr.tipo === 'Egreso') return -monto;
    if (instr.tipo === 'Suscripcion FCI') return -monto;
    if (instr.tipo === 'Rescate FCI') {
        if (instr.plazoLiquidacion === 'T+1') {
            const fechaLiq = instr.fechaLiquidacion || '';
            if (!fechaLiq) return monto; // legacy sin campo: asumir T+0
            return fechaLiq <= fechaHoyIso ? monto : 0;
        }
        return monto;
    }
    return 0;
};

// Tipos de instrucción que mueven efectivo. Las transferencias de títulos no
// tocan el saldo del comitente.
export const TIPOS_INSTR_DINERO = ['Ingreso', 'Egreso', 'Suscripcion FCI', 'Rescate FCI'];

export const calculateSaldos = ({
    snapshot,
    cauciones = [],
    pases = [],
    operaciones = [],
    instrucciones = [],
    acreditaciones = null,
    licitaciones = [],
    moneda = BUCKET_ARS,
    clientesDb = {},
    fechaHoy = null,
}) => {
    const monedaSaldoField = SNAPSHOT_SALDO_FIELD[moneda];
    const monedaMovimiento = MONEDA_MOVIMIENTO[moneda];
    const fechaHoyIso = fechaHoy || toIsoDate(new Date());

    const result = new Map();

    const ensureEntry = (nroComitente, nombre) => {
        if (result.has(nroComitente)) return result.get(nroComitente);
        const entry = {
            nroComitente,
            nombre: nombre || clientesDb[nroComitente] || 'N/A',
            saldoSnapshot: 0,
            saldoReal: 0,
            saldoProyectado: 0,
            movimientosReales: [],
            movimientosPendientes: [],
        };
        result.set(nroComitente, entry);
        return entry;
    };

    if (snapshot && Array.isArray(snapshot.comitentes)) {
        snapshot.comitentes.forEach(s => {
            const saldoSnap = Number(s[monedaSaldoField]) || 0;
            const entry = ensureEntry(s.nroComitente, s.nombre);
            entry.saldoSnapshot = saldoSnap;
            entry.saldoReal = saldoSnap;
            entry.saldoProyectado = saldoSnap;
        });
    }

    const pushMovimiento = (entry, isReal, movimiento) => {
        if (isReal) {
            entry.movimientosReales.push(movimiento);
            entry.saldoReal += movimiento.monto;
            entry.saldoProyectado += movimiento.monto;
        } else {
            entry.movimientosPendientes.push(movimiento);
            entry.saldoProyectado += movimiento.monto;
        }
    };

    // Cauciones y pases comparten la misma mecánica de impacto sobre el efectivo:
    // Tomadora suma, Colocadora resta; Hechas por montoOperado, Pendientes por monto.
    const procesarFinanciacion = (items, tipo, tipoLabel) => {
        items.forEach(c => {
            if (c.moneda !== monedaMovimiento) return;
            const esReal = isCaucionReal(c.estado) && debeContarPostSnapshot(c.createdAt, snapshot);
            const esPendiente = !esReal && isPendiente(c.estado);
            if (!esReal && !esPendiente) return;
            // Reales (Hechas): impactan por lo efectivamente operado por el trader.
            // Pendientes/En Proceso: impactan por el monto cargado por el comercial (montoOperado aún no existe).
            const montoBase = esReal
                ? (Number(c.montoOperado) || Number(c.monto) || 0)
                : (Number(c.monto) || 0);
            if (!montoBase) return;
            const monto = c.operacion === 'Tomadora' ? montoBase : -montoBase;
            pushMovimiento(ensureEntry(c.nroComitente, c.cliente), esReal, {
                id: c.id, tipo, subtipo: c.operacion,
                estado: c.estado, monto, descripcion: `${tipoLabel} ${c.operacion}`,
                createdAt: c.createdAt,
            });
        });
    };

    procesarFinanciacion(cauciones, 'caucion', 'Caución');
    procesarFinanciacion(pases, 'pase', 'Pase');

    operaciones.forEach(op => {
        if (op.moneda !== monedaMovimiento) return;
        if (op.plazo !== 'C.I.') return;
        if (!operacionAfectaSaldo(op)) return;
        const monto = impactoOperacion(op);
        if (!monto) return;
        const desc = `${op.operacion} ${op.especie || ''}`.trim();
        if (isOperacionReal(op.estado) && debeContarPostSnapshot(op.createdAt, snapshot)) {
            pushMovimiento(ensureEntry(op.nroComitente, op.cliente), true, {
                id: op.id, tipo: 'operacion', subtipo: op.operacion,
                estado: op.estado, monto, descripcion: desc,
                createdAt: op.createdAt,
            });
        } else if (isPendiente(op.estado)) {
            pushMovimiento(ensureEntry(op.nroComitente, op.cliente), false, {
                id: op.id, tipo: 'operacion', subtipo: op.operacion,
                estado: op.estado, monto, descripcion: desc,
                createdAt: op.createdAt,
            });
        }
    });

    instrucciones.forEach(instr => {
        if (instr.moneda !== monedaMovimiento) return;
        if (!TIPOS_INSTR_DINERO.includes(instr.tipo)) return;
        const monto = impactoInstruccion(instr, fechaHoyIso);
        if (!monto) return;
        const fondoLabel = instr.fci ? ` ${instr.fci}` : '';
        const plazoLabel = instr.plazoLiquidacion ? ` (${instr.plazoLiquidacion})` : '';
        const desc = `${instr.tipo}${fondoLabel}${plazoLabel}`;
        if (isInstruccionReal(instr.estado) && debeContarPostSnapshot(instr.createdAt, snapshot)) {
            pushMovimiento(ensureEntry(instr.nroComitente, instr.cliente), true, {
                id: instr.id, tipo: 'instruccion', subtipo: instr.tipo,
                estado: instr.estado, monto, descripcion: desc,
                createdAt: instr.createdAt,
                plazoLiquidacion: instr.plazoLiquidacion,
                fechaLiquidacion: instr.fechaLiquidacion,
            });
        } else if (isPendiente(instr.estado)) {
            pushMovimiento(ensureEntry(instr.nroComitente, instr.cliente), false, {
                id: instr.id, tipo: 'instruccion', subtipo: instr.tipo,
                estado: instr.estado, monto, descripcion: desc,
                createdAt: instr.createdAt,
                plazoLiquidacion: instr.plazoLiquidacion,
                fechaLiquidacion: instr.fechaLiquidacion,
            });
        }
    });

    forEachAcreditada(acreditaciones, moneda, (nroComitente, nombre, ev) => {
        if (!debeContarPostSnapshot(ev.marcadoAt, snapshot)) return;
        pushMovimiento(ensureEntry(nroComitente, nombre), true, movimientoDeAcreditacion(ev));
    });

    forEachDebitoLicitacion(licitaciones, monedaMovimiento, (debito) => {
        pushMovimiento(ensureEntry(debito.nroComitente, debito.cliente), true, movimientoDeLicitacion(debito));
    });

    return result;
};

/*
 * Recorre los eventos de acreditaciones esperadas (Map nroComitente -> { nombre, eventos },
 * ver src/utils/acreditacionesEsperadas.js) marcados 'Acreditada' y con monto en `moneda`.
 */
const forEachAcreditada = (acreditaciones, moneda, callback) => {
    if (!acreditaciones || typeof acreditaciones.forEach !== 'function') return;
    acreditaciones.forEach((entry, nroComitente) => {
        (entry?.eventos || []).forEach(ev => {
            if (ev.bucket !== moneda) return;
            if (ev.estado !== ESTADO_ACREDITACION_ACREDITADA) return;
            if (typeof ev.monto !== 'number' || !ev.monto) return;
            callback(String(nroComitente), entry.nombre, ev);
        });
    });
};

const movimientoDeAcreditacion = (ev) => ({
    id: ev.eventId, tipo: 'acreditacion', subtipo: ev.tipo,
    estado: 'Acreditada', monto: ev.monto,
    descripcion: [ev.concepto, ev.instrumento].filter(Boolean).join(' '),
    createdAt: ev.marcadoAt,
});

// Débitos de licitaciones (ver buildDebitosLicitaciones en licitacionesCalculator) en `monedaMovimiento`.
const forEachDebitoLicitacion = (licitaciones, monedaMovimiento, callback) => {
    (licitaciones || []).forEach(debito => {
        if (debito.moneda !== monedaMovimiento) return;
        if (!Number(debito.monto)) return;
        callback(debito);
    });
};

const movimientoDeLicitacion = (debito) => ({
    id: debito.id, tipo: 'licitacion', subtipo: 'Adjudicada',
    estado: 'Adjudicada', monto: Number(debito.monto),
    descripcion: debito.descripcion,
    licitacionId: debito.licitacionId,
});

/*
 * Agrega los movimientos ejecutados ('Hecha') de TODO el día por tipo, sin el
 * filtro post-snapshot de calculateSaldos: con un snapshot intradiario activo,
 * los movimientos previos ya están reflejados en el snapshot y no aparecen en
 * movimientosReales, pero el dashboard necesita el desglose del día completo.
 * Brutos en valor absoluto; netos con signo (+ acreedor, - deudor).
 */
const sumCaucionesDelDia = (cauciones, monedaMovimiento, tipo = 'caucion', tipoLabel = 'Caución') => {
    const acc = { tomadoras: 0, colocadoras: 0, neto: 0, cantidad: 0, items: [] };
    cauciones.forEach(c => {
        if (c.moneda !== monedaMovimiento) return;
        if (!isCaucionReal(c.estado)) return;
        const monto = Number(c.montoOperado) || Number(c.monto) || 0;
        if (!monto) return;
        if (c.operacion === 'Tomadora') acc.tomadoras += monto;
        else acc.colocadoras += monto;
        const montoConSigno = c.operacion === 'Tomadora' ? monto : -monto;
        acc.neto += montoConSigno;
        acc.cantidad += 1;
        acc.items.push({
            id: c.id, tipo, nroComitente: c.nroComitente, cliente: c.cliente,
            descripcion: `${tipoLabel} ${c.operacion}`, estado: c.estado,
            monto: montoConSigno, createdAt: c.createdAt,
        });
    });
    return acc;
};

const sumOperacionesCIDelDia = (operaciones, monedaMovimiento) => {
    const acc = { ventas: 0, compras: 0, neto: 0, cantidad: 0, items: [] };
    operaciones.forEach(op => {
        if (op.moneda !== monedaMovimiento) return;
        if (op.plazo !== 'C.I.') return;
        if (!operacionAfectaSaldo(op)) return;
        if (!isOperacionReal(op.estado)) return;
        const monto = impactoOperacion(op);
        if (!monto) return;
        if (monto > 0) acc.ventas += monto;
        else acc.compras += -monto;
        acc.neto += monto;
        acc.cantidad += 1;
        acc.items.push({
            id: op.id, tipo: 'operacion', nroComitente: op.nroComitente, cliente: op.cliente,
            descripcion: `${op.operacion} ${op.especie || ''}`.trim(), estado: op.estado,
            monto, createdAt: op.createdAt,
        });
    });
    return acc;
};

const sumInstruccionesDelDia = (instrucciones, monedaMovimiento, fechaHoyIso) => {
    const dinero = { ingresos: 0, egresos: 0, neto: 0, cantidad: 0, items: [] };
    const fci = { rescates: 0, suscripciones: 0, neto: 0, cantidad: 0, items: [] };
    instrucciones.forEach(instr => {
        if (instr.moneda !== monedaMovimiento) return;
        if (!TIPOS_INSTR_DINERO.includes(instr.tipo)) return;
        if (!isInstruccionReal(instr.estado)) return;
        const monto = impactoInstruccion(instr, fechaHoyIso);
        if (!monto) return;
        const esFci = instr.tipo === 'Suscripcion FCI' || instr.tipo === 'Rescate FCI';
        const acc = esFci ? fci : dinero;
        if (esFci) {
            if (monto > 0) acc.rescates += monto;
            else acc.suscripciones += -monto;
        } else if (monto > 0) {
            acc.ingresos += monto;
        } else {
            acc.egresos += -monto;
        }
        acc.neto += monto;
        acc.cantidad += 1;
        const fondoLabel = instr.fci ? ` ${instr.fci}` : '';
        const plazoLabel = instr.plazoLiquidacion ? ` (${instr.plazoLiquidacion})` : '';
        acc.items.push({
            id: instr.id, tipo: 'instruccion', nroComitente: instr.nroComitente, cliente: instr.cliente,
            descripcion: `${instr.tipo}${fondoLabel}${plazoLabel}`, estado: instr.estado,
            monto, createdAt: instr.createdAt,
        });
    });
    return { ingresosEgresos: dinero, fci };
};

const sumLicitacionesDelDia = (licitaciones, monedaMovimiento) => {
    const acc = { debitos: 0, neto: 0, cantidad: 0, items: [] };
    forEachDebitoLicitacion(licitaciones, monedaMovimiento, (debito) => {
        const movimiento = movimientoDeLicitacion(debito);
        acc.debitos += -movimiento.monto;
        acc.neto += movimiento.monto;
        acc.cantidad += 1;
        acc.items.push({ ...movimiento, nroComitente: debito.nroComitente, cliente: debito.cliente });
    });
    return acc;
};

export const aggregateMovimientosDelDia = ({
    cauciones = [],
    pases = [],
    operaciones = [],
    instrucciones = [],
    licitaciones = [],
    moneda = BUCKET_ARS,
    fechaHoy = null,
}) => {
    const monedaMovimiento = MONEDA_MOVIMIENTO[moneda];
    const fechaHoyIso = fechaHoy || toIsoDate(new Date());
    const caucionesAgg = sumCaucionesDelDia(cauciones, monedaMovimiento);
    const pasesAgg = sumCaucionesDelDia(pases, monedaMovimiento, 'pase', 'Pase');
    const operacionesCI = sumOperacionesCIDelDia(operaciones, monedaMovimiento);
    const { ingresosEgresos, fci } = sumInstruccionesDelDia(instrucciones, monedaMovimiento, fechaHoyIso);
    const licitacionesAgg = sumLicitacionesDelDia(licitaciones, monedaMovimiento);
    return {
        cauciones: caucionesAgg,
        pases: pasesAgg,
        operacionesCI,
        ingresosEgresos,
        fci,
        licitaciones: licitacionesAgg,
        totalNeto: caucionesAgg.neto + pasesAgg.neto + operacionesCI.neto + ingresosEgresos.neto + fci.neto
            + licitacionesAgg.neto,
    };
};

/*
 * Totales agregados de un Map producido por calculateSaldos.
 */
export const sumSaldosTotales = (resultMap) => {
    const totals = {
        totalSnapshot: 0,
        totalReal: 0,
        totalProyectado: 0,
        totalAcreedor: 0,
        totalDeudor: 0,
        pendientes: { monto: 0, cantidad: 0 },
    };
    resultMap.forEach(entry => {
        totals.totalSnapshot += entry.saldoSnapshot;
        totals.totalReal += entry.saldoReal;
        totals.totalProyectado += entry.saldoProyectado;
        if (entry.saldoReal > 0) totals.totalAcreedor += entry.saldoReal;
        if (entry.saldoReal < 0) totals.totalDeudor += entry.saldoReal;
        totals.pendientes.monto += entry.saldoProyectado - entry.saldoReal;
        totals.pendientes.cantidad += entry.movimientosPendientes.length;
    });
    return totals;
};

/*
 * Convierte el Map a un array, filtrando cuentas sin saldo ni movimientos.
 */
export const toArrayConSaldo = (resultMap) => {
    return Array.from(resultMap.values()).filter(d =>
        d.saldoSnapshot !== 0 || d.saldoReal !== 0 || d.saldoProyectado !== 0
        || d.movimientosReales.length > 0 || d.movimientosPendientes.length > 0
    );
};

/*
 * Compara dos snapshots (anterior y nuevo) y detecta diferencias por comitente
 * entre el delta esperado (saldoNuevo - saldoAnterior) y el delta calculado
 * por la suma de movimientos Hechos entre los dos uploadedAt.
 *
 * Devuelve { ARS: [...diferencias], USD: [...], CABLE: [...] }
 * Una diferencia se reporta si |diferencia| > toleranciaAbsoluta (default 0.01).
 *
 * Los débitos de licitaciones no entran: el custodio no los mueve entre dos
 * snapshots de la ventana de caja, así que contarlos daría una diferencia falsa
 * en cada intradiario para todo cliente adjudicado.
 */
export const conciliarSnapshots = ({
    snapshotAnterior,
    snapshotNuevo,
    cauciones = [],
    pases = [],
    operaciones = [],
    instrucciones = [],
    acreditaciones = null,
    toleranciaAbsoluta = 0.01,
}) => {
    const empty = MONEDA_BUCKETS.reduce((acc, b) => ({ ...acc, [b]: [] }), {});
    if (!snapshotAnterior || !snapshotNuevo) return empty;
    const desdeMs = toMillis(snapshotAnterior.uploadedAt);
    const hastaMs = toMillis(snapshotNuevo.uploadedAt);
    if (!hastaMs) return empty;

    const inRange = (ts) => {
        const ms = toMillis(ts);
        return ms > desdeMs && ms <= hastaMs;
    };

    const sumPorComitente = (moneda) => {
        const monedaMov = MONEDA_MOVIMIENTO[moneda];
        const sums = new Map();

        cauciones.forEach(c => {
            if (c.moneda !== monedaMov) return;
            if (!isCaucionReal(c.estado)) return;
            if (!inRange(c.createdAt)) return;
            const monto = impactoCaucion(c);
            sums.set(c.nroComitente, (sums.get(c.nroComitente) || 0) + monto);
        });

        pases.forEach(p => {
            if (p.moneda !== monedaMov) return;
            if (!isCaucionReal(p.estado)) return;
            if (!inRange(p.createdAt)) return;
            const monto = impactoCaucion(p);
            sums.set(p.nroComitente, (sums.get(p.nroComitente) || 0) + monto);
        });

        operaciones.forEach(op => {
            if (op.moneda !== monedaMov) return;
            if (op.plazo !== 'C.I.') return;
            if (!isOperacionReal(op.estado)) return;
            if (!operacionAfectaSaldo(op)) return;
            if (!inRange(op.createdAt)) return;
            const monto = impactoOperacion(op);
            sums.set(op.nroComitente, (sums.get(op.nroComitente) || 0) + monto);
        });

        const fechaHoyIso = toIsoDate(new Date());
        instrucciones.forEach(instr => {
            if (instr.moneda !== monedaMov) return;
            if (!TIPOS_INSTR_DINERO.includes(instr.tipo)) return;
            if (!isInstruccionReal(instr.estado)) return;
            if (!inRange(instr.createdAt)) return;
            const monto = impactoInstruccion(instr, fechaHoyIso);
            if (!monto) return;
            sums.set(instr.nroComitente, (sums.get(instr.nroComitente) || 0) + monto);
        });

        forEachAcreditada(acreditaciones, moneda, (nroComitente, _nombre, ev) => {
            if (!inRange(ev.marcadoAt)) return;
            sums.set(nroComitente, (sums.get(nroComitente) || 0) + ev.monto);
        });

        return sums;
    };

    const procesar = (moneda) => {
        const field = SNAPSHOT_SALDO_FIELD[moneda];
        const sums = sumPorComitente(moneda);
        const anteriorMap = new Map();
        (snapshotAnterior.comitentes || []).forEach(c => {
            anteriorMap.set(c.nroComitente, Number(c[field]) || 0);
        });

        const diferencias = [];
        (snapshotNuevo.comitentes || []).forEach(c => {
            const saldoAnt = anteriorMap.get(c.nroComitente) || 0;
            const saldoNuevo = Number(c[field]) || 0;
            const deltaEsperado = saldoNuevo - saldoAnt;
            const deltaCalculado = sums.get(c.nroComitente) || 0;
            const diferencia = deltaEsperado - deltaCalculado;
            if (Math.abs(diferencia) > toleranciaAbsoluta) {
                diferencias.push({
                    nroComitente: c.nroComitente,
                    nombre: c.nombre || '',
                    saldoAnterior: saldoAnt,
                    saldoNuevo,
                    deltaEsperado,
                    deltaCalculado,
                    diferencia,
                });
            }
        });

        // Comitentes que estaban en el anterior y no en el nuevo (raro pero posible)
        anteriorMap.forEach((saldoAnt, nroComitente) => {
            if ((snapshotNuevo.comitentes || []).some(c => c.nroComitente === nroComitente)) return;
            if (saldoAnt === 0) return;
            const deltaEsperado = -saldoAnt;
            const deltaCalculado = sums.get(nroComitente) || 0;
            const diferencia = deltaEsperado - deltaCalculado;
            if (Math.abs(diferencia) > toleranciaAbsoluta) {
                diferencias.push({
                    nroComitente,
                    nombre: '(sin nombre)',
                    saldoAnterior: saldoAnt,
                    saldoNuevo: 0,
                    deltaEsperado,
                    deltaCalculado,
                    diferencia,
                    notas: 'Comitente no presente en snapshot nuevo',
                });
            }
        });

        return diferencias;
    };

    return MONEDA_BUCKETS.reduce((acc, bucket) => ({ ...acc, [bucket]: procesar(bucket) }), {});
};
